import React, { createContext, useEffect, useMemo, useState } from "react";
import {
  createTheme,
  darken,
  lighten,
  PaletteColor,
  PaletteColorOptions,
  ThemeProvider,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

declare module "@mui/material/styles" {
  interface Palette {
    blue: PaletteColor;
    skyBlue: PaletteColor;
    purple: PaletteColor;
    mintGreen: PaletteColor;
    green: PaletteColor;
    orange: PaletteColor;
    goldenYellow: PaletteColor;
    brown: PaletteColor;
    blushPink: PaletteColor;
    lavenderBlue: PaletteColor;
    red: PaletteColor;

    customColors: {
      lightYellow: string;
      lightBlue: string;
      darkBlue: string;
      pinkLight: string;
      pinkDark: string;
      greenLight: string;
      greensuperLight: string;
      greenDark: string;
      EmeraldGreen: string;
      greyTextColor: string;
      purple: string;
      backdrop: string;
      tableGrayHeadingColor: string;
      grayBackGroundColor: string;
      white: string;
      blackBorder: string;
      greyBorder: string;
      borderErrorRedColor: string;
      disabledBorderColor: string;
      darkBlackBorder: string;
      lightPurple: string;
      greyDarkborder: string;
      greyBackground: string;
      blackBodyBackgorund: string;
      greyBodyBackground: string;
      lightGreyColor: string;
      lightred: string;
      darkBlueText: string;
      purpleMain: string;
      lightOrange: string;
      skyLight: string;
      darkBlueGrey: string;
      text: string;
      blackText: string;
      BlueLightTextColor: string;
      tealGradientStart: string;
      tealGradientEnd: string;
      purpleGradientStart: string;
      purpleGradientEnd: string;
      blueGradientStart: string;
      blueGradientEnd: string;
      blueGradientHoverStart: string;
      blueGradientHoverEnd: string;
      lightBorder: string;
      subtleOverlay: string;

      dividerGradientTransparent: string;
      dividerGradientMid: string;

      lightGreenBackground: string;
      // Swagstore specific colors
      lightGrayBackground: string;
      veryLightGray: string;
      lightGrayBorder: string;
      mediumGrayText: string;
      hoverGray: string;
      info: string;
      infoLightBorder: string;
      infoLightBackground: string;
      successGreen: string;
      successGreenLightBorder: string;
      successGreenLightBackground: string;
      purpleDark: string;
      teal: string;
      purpleLight: string;
      pink: string;
      grayText: string;
      errorRed: string;
      lightGray: string;
      blueLightBackground: string;
      mediumGray: string;
      switchTrackGray: string;
      shadowLight: string;
      shadowMedium: string;
      shadowVeryLight: string;
      shadowLightBorder: string;
      cardBackground: string;
      veryLightPurple: string;
    };
  }

  interface PaletteOptions {
    blue?: PaletteColorOptions;
    skyBlue?: PaletteColorOptions;
    purple?: PaletteColorOptions;
    mintGreen?: PaletteColorOptions;
    green?: PaletteColorOptions;
    orange?: PaletteColorOptions;
    goldenYellow?: PaletteColorOptions;
    brown?: PaletteColorOptions;
    blushPink?: PaletteColorOptions;
    lavenderBlue?: PaletteColorOptions;
    red?: PaletteColorOptions;
    customColors?: {
      lightYellow?: string;
      lightBlue?: string;
      darkBlue?: string;
      pinkLight?: string;
      pinkDark?: string;
      greenLight?: string;
      greensuperLight?: string;
      greenDark?: string;
      EmeraldGreen?: string;
      greyTextColor?: string;
      purple?: string;
      backdrop?: string;
      tableGrayHeadingColor?: string;
      grayBackGroundColor?: string;
      white?: string;
      blackBorder?: string;
      greyBorder?: string;
      borderErrorRedColor?: string;
      disabledBorderColor?: string;
      darkBlackBorder?: string;
      lightPurple?: string;
      greyDarkborder?: string;
      greyBackground?: string;
      blackBodyBackgorund: string;
      greyBodyBackground: string;
      lightGreyColor: string;
      lightred: string;
      darkBlueText: string;
      purpleMain: string;
      lightOrange: string;
      skyLight: string;
      darkBlueGrey: string;
      BlueLightTextColor?: string;
      text?: string;
      black?: string;
      tealGradientStart?: string;
      tealGradientEnd?: string;
      purpleGradientStart?: string;
      purpleGradientEnd?: string;
      blueGradientStart?: string;
      blueGradientEnd?: string;
      blueGradientHoverStart?: string;
      blueGradientHoverEnd?: string;
      lightBorder?: string;
      subtleOverlay?: string;
      dividerGradientTransparent?: string;
      dividerGradientMid?: string;
      lightGreenBackground?: string;
      // Swagstore specific colors
      lightGrayBackground?: string;
      veryLightGray?: string;
      lightGrayBorder?: string;
      mediumGrayText?: string;
      hoverGray?: string;
      info?: string;
      infoLightBorder?: string;
      infoLightBackground?: string;
      successGreen?: string;
      successGreenLightBorder?: string;
      successGreenLightBackground?: string;
      purpleDark?: string;
      teal?: string;
      purpleLight?: string;
      pink?: string;
      grayText?: string;
      errorRed?: string;
      lightGray?: string;
      blueLightBackground?: string;
      mediumGray?: string;
      switchTrackGray?: string;
      shadowLight?: string;
      shadowMedium?: string;
      shadowVeryLight?: string;
      shadowLightBorder?: string;
      cardBackground?: string;
      blackText?: string;
      veryLightPurple?: string;
    };
  }
}
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    tertiary: true;
    grayscale: true;
    primaryBlock: true;
    secondaryBlock: true;
    tertiaryBlock: true;
    grayscaleBlock: true;
    transparentPrimary: true;
    transparentSecondary: true;
    done: true;
    cancel: true;
  }
}
interface ThemeContextType {
  themeName: string;
  setThemeName: any;
}
export type AppButtonVariants =
  | "primary"
  | "secondary"
  | "tertiary"
  | "grayscale"
  | "transparentPrimary"
  | "primaryBlock"
  | "secondaryBlock"
  | "tertiaryBlock"
  | "grayscaleBlock"
  | "transparentPrimary"
  | "transparentSecondary"
  | "done"
  | "cancel";

const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1e1665" },
    blue: {
      main: "#4049B1",
      light: lighten("#4049B1", 0.3),
      dark: darken("#4049B1", 0.3),
    },
    skyBlue: {
      main: "#4285F4",
      light: lighten("#4285F4", 0.3),
      dark: darken("#4285F4", 0.3),
    },
    purple: {
      main: "#8884D8",
      light: lighten("#8884D8", 0.3),
      dark: darken("#8884D8", 0.3),
    },
    mintGreen: {
      main: "#50DFC3",
      light: lighten("#50DFC3", 0.3),
      dark: darken("#50DFC3", 0.3),
    },
    green: {
      main: "#6CBA5C",
      light: lighten("#6CBA5C", 0.3),
      dark: darken("#6CBA5C", 0.3),
    },
    orange: {
      main: "#FF8042",
      light: lighten("#FF8042", 0.3),
      dark: darken("#FF8042", 0.3),
    },
    goldenYellow: {
      main: "#FFBB28",
      light: lighten("#FFBB28", 0.3),
      dark: darken("#FFBB28", 0.3),
    },
    brown: {
      main: "#FFE5B5",
      light: lighten("#FFE5B5", 0.3),
      dark: darken("#FFE5B5", 0.3),
    },
    blushPink: {
      main: "#FFCECE",
      light: lighten("#FFCECE", 0.3),
      dark: darken("#FFCECE", 0.3),
    },
    lavenderBlue: {
      main: "#D1CCFF",
      light: lighten("#D1CCFF", 0.3),
      dark: darken("#D1CCFF", 0.3),
    },
    red: {
      main: "#FF0000",
      light: lighten("#FF0000", 0.3),
      dark: darken("#FF0000", 0.3),
    },

    customColors: {
      lightYellow: "#F3EFDF",
      lightBlue: "#EDF0FF",
      darkBlue: "#4D69FA",
      pinkLight: "#FFDAED",
      pinkDark: "#D7458F",
      greenLight: "#CCFFF5",
      greensuperLight: "#DFF3E4",
      greenDark: "#52E6CA",
      EmeraldGreen: "#02C875",
      greyTextColor: "#7C7A7F",
      purple: "#6455ED",
      backdrop: "rgba(0, 0, 0, 0.1)",
      tableGrayHeadingColor: "#93969B",
      grayBackGroundColor: "#F2F2F2",
      white: "#FFFFFF",
      blackBorder: "#2A282E",
      greyBorder: "rgba(224, 224, 224, 1)",
      borderErrorRedColor: "#d32f2f",
      disabledBorderColor: "#E0E0E0",
      darkBlackBorder: "#000",
      lightPurple: "#E5E4FB",
      greyDarkborder: "#8B8B8B",
      greyBackground: "#ECECEC",
      blackBodyBackgorund: "#121212",
      greyBodyBackground: "#F3F3F3",
      lightGreyColor: "#F3F4F6",
      lightred: "#E96767",
      darkBlueText: "#1B2559",
      purpleMain: "#6455ED",
      lightOrange: "#ffe5c0",
      skyLight: "#00F0FF",
      darkBlueGrey: "#0F172A",
      text: "#333333",
      black: "#121212",
      BlueLightTextColor: "#B0ADCA",
      tealGradientStart: "#009688",
      tealGradientEnd: "#00A896",
      purpleGradientStart: "#8E2DE2",
      purpleGradientEnd: "#FF0080",
      blueGradientStart: "#2196F3",
      blueGradientEnd: "#3F51B5",
      blueGradientHoverStart: "#1976D2",
      blueGradientHoverEnd: "#303F9F",
      lightBorder: "#eee",
      subtleOverlay: "rgba(0,0,0,0.02)",
      dividerGradientTransparent: "rgba(128, 128, 128, 0.0)",
      dividerGradientMid: "rgba(128, 128, 128, 0.4)",
      lightGreenBackground: "#E9FBF3",
      // Swagstore specific colors
      lightGrayBackground: "#F9F9F9",
      veryLightGray: "#F5F5F5",
      lightGrayBorder: "#ddd",
      mediumGrayText: "#666",
      hoverGray: "#f0f0f0",
      info: "#4D69FA",
      infoLightBorder: "#D1FAE5",
      infoLightBackground: "#ECFDF5",
      successGreen: "#10B981",
      successGreenLightBorder: "#D1FAE5",
      successGreenLightBackground: "#ECFDF5",
      purpleDark: "#7C3AED",
      teal: "#4FD1C5",
      purpleLight: "#A78BFA",
      pink: "#F687B3",
      grayText: "#6B7280",
      errorRed: "#EF4444",
      lightGray: "#E5E7EB",
      blueLightBackground: "#EFF6FF",
      mediumGray: "#9CA3AF",
      switchTrackGray: "#D1D5DB",
      shadowLight: "rgba(0,0,0,0.12)",
      shadowMedium: "rgba(0,0,0,0.1)",
      shadowVeryLight: "rgba(0, 0, 0, 0.05)",
      shadowLightBorder: "rgba(0, 0, 0, 0.08)",
      cardBackground: "#f8fafc",
    },
  },
  typography: {
    fontFamily: "'Inter', 'Open Sans', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 600,
          borderRadius: "6px",
          padding: "6px 14px",
          minHeight: "34px",
          fontFamily: "'Inter', sans-serif",
          letterSpacing: "-0.005em",
        },
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            backgroundColor: "#4D69FA",
            color: "#fff",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#4D69FA",
              color: "#fff",
              boxShadow: "0px 4px 10px rgba(77, 105, 250, 0.4)",
              transform: "translateY(-2px)", // subtle lift effect (optional)
            },
            "&:disabled": {
              backgroundColor: "#4D69FA",
              color: "#fff",
              opacity: 0.4,
            },
          },
        },
        {
          props: { variant: "done" },
          style: {
            backgroundColor: "#28A745",
            color: "#fff",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#28A745",
              color: "#fff",
              boxShadow: "0px 4px 10px rgba(40, 167, 69, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "cancel" },
          style: {
            backgroundColor: "#fff",
            border: "1px solid #DC3545",
            color: "#DC3545",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#fff",
              color: "#DC3545",
              border: "1px solid #DC3545",
              boxShadow: "0px 4px 10px rgba(220, 53, 69, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            backgroundColor: "#ffcfe7",
            color: "#D7458F",
            "&:hover": {
              backgroundColor: "#D7458F",
              color: "#fff",
              boxShadow: "0px 4px 10px rgba(215, 69, 143, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "tertiary" },
          style: {
            backgroundColor: "#CCFFF5",
            color: "#52E6CA",
            "&:hover": {
              backgroundColor: "#52E6CA",
              color: "#fff",
              boxShadow: "0px 4px 10px rgba(82, 230, 202, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "grayscale" },
          style: {
            backgroundColor: "#fff",
            opacity: 1,
            border: "1px solid #86898f",
            color: "#86898f",
            "&:hover": {
              backgroundColor: "#86898f",
              color: "#fff",
              opacity: 1,
              boxShadow: "0px 4px 10px rgba(86, 137, 143, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:disabled": {
              backgroundColor: "#fff",
              border: "1px solid #93969B",
              color: "#93969B",
              opacity: 0.7,
            },
          },
        },

        {
          props: { variant: "primaryBlock" },
          style: {
            backgroundColor: "#4D69FA",
            color: "#fff",
            opacity: 1,
            "&:hover": {
              backgroundColor: "#4D69FA",
              color: "#fff",
              opacity: 1,
              boxShadow: "0px 4px 10px rgba(77, 105, 250, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:disabled": {
              backgroundColor: "#4D69FA",
              color: "#fff",
              opacity: 0.4,
            },
          },
        },
        {
          props: { variant: "secondaryBlock" },
          style: {
            backgroundColor: "#FFB0D8",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#D7458F",
              color: "#fff",
              boxShadow: "0px 4px 10px rgba(215, 69, 143, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:disabled": {
              backgroundColor: "#FFB0D8",
              color: "#fff",
              opacity: 0.4,
            },
          },
        },
        {
          props: { variant: "tertiaryBlock" },
          style: {
            backgroundColor: "#CCFFF5",
            color: "#52E6CA",
            "&:hover": {
              backgroundColor: "#52E6CA",
              color: "#fff",
              boxShadow: "0px 4px 10px rgba(82, 230, 202, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:disabled": {
              backgroundColor: "#CCFFF5",
              color: "#52E6CA",
              opacity: 0.4,
            },
          },
        },
        {
          props: { variant: "grayscaleBlock" },
          style: {
            backgroundColor: "#93969B",
            color: "#fff",
            border: "1px solid #93969B",
            opacity: 1,
            "&:hover": {
              backgroundColor: "#93969B",
              color: "#fff",
              opacity: 1,
              boxShadow: "0px 4px 10px rgba(147, 150, 155, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:disabled": {
              opacity: 0.8,
              backgroundColor: "#93969B",
              color: "#fff",
            },
          },
        },
        {
          props: { variant: "transparentPrimary" },
          style: {
            backgroundColor: "transparent",
            color: "#4D69FA", // MUI default blue
            opacity: 1,
            border: "1px solid #4D69FA",
            boxShadow: "none",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#4D69FA",
              opacity: 1,
              boxShadow: "0px 4px 10px rgba(77, 105, 250, 0.4)",
              transform: "translateY(-2px)", // subtle lift effect (optional)
            },
            "&:disabled": {
              backgroundColor: "transparent",
              color: "#4D69FA",
              opacity: 0.5,
            },
          },
        },
        {
          props: { variant: "transparentSecondary" },
          style: {
            backgroundColor: "transparent",
            color: "#D7458F", // MUI default blue
            opacity: 1,
            border: "1px solid #D7458F",
            boxShadow: "none",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#D7458F",
              opacity: 1,
              boxShadow: "0px 4px 10px rgba(215, 69, 143, 0.4)",
              transform: "translateY(-2px)", // subtle lift effect (optional)
            },
            "&:disabled": {
              backgroundColor: "transparent",
              color: "#D7458F",
              opacity: 0.5,
            },
          },
        },
      ],
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          '&[role="menu"]': {
            paddingTop: 10,
            paddingBottom: 10,
            backgroundColor: "#F9F9F9",
            boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3)",
          },
        },
      },
    },
  },
});
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#90caf9" },
    blue: {
      main: "#4049B1",
      light: lighten("#4049B1", 0.3),
      dark: darken("#4049B1", 0.3),
    },
    skyBlue: {
      main: "#4285F4",
      light: lighten("#4285F4", 0.3),
      dark: darken("#4285F4", 0.3),
    },
    purple: {
      main: "#8884D8",
      light: lighten("#8884D8", 0.3),
      dark: darken("#8884D8", 0.3),
    },
    mintGreen: {
      main: "#50DFC3",
      light: lighten("#50DFC3", 0.3),
      dark: darken("#50DFC3", 0.3),
    },
    green: {
      main: "#6CBA5C",
      light: lighten("#6CBA5C", 0.3),
      dark: darken("#6CBA5C", 0.3),
    },
    orange: {
      main: "#FF8042",
      light: lighten("#FF8042", 0.3),
      dark: darken("#FF8042", 0.3),
    },
    goldenYellow: {
      main: "#FFBB28",
      light: lighten("#FFBB28", 0.3),
      dark: darken("#FFBB28", 0.3),
    },
    brown: {
      main: "#FFE5B5",
      light: lighten("#FFE5B5", 0.3),
      dark: darken("#FFE5B5", 0.3),
    },
    blushPink: {
      main: "#FFCECE",
      light: lighten("#FFCECE", 0.3),
      dark: darken("#FFCECE", 0.3),
    },
    lavenderBlue: {
      main: "#D1CCFF",
      light: lighten("#D1CCFF", 0.3),
      dark: darken("#D1CCFF", 0.3),
    },
    red: {
      main: "#FF0000",
      light: lighten("#FF0000", 0.3),
      dark: darken("#FF0000", 0.3),
    },

    customColors: {
      lightYellow: "#F3EFDF",
      lightBlue: "#EDF0FF",
      darkBlue: "#4D69FA",
      pinkLight: "#FFB0D8",
      pinkDark: "#D7458F",
      greenLight: "#CCFFF5",
      greensuperLight: "#DFF3E4",
      greenDark: "#52E6CA",
      EmeraldGreen: "#02C875",
      greyTextColor: "#7C7A7F",
      backdrop: "rgba(0, 0, 0, 0.1)",
      blackBodyBackgorund: "#121212",
      greyBodyBackground: "#F3F3F3",
      lightGreyColor: "#F3F4F6",
      lightred: "#E96767",
      darkBlueText: "#1B2559",
      purpleMain: "#6455ED",
      lightOrange: "#ffe5c0",
      skyLight: "#00F0FF",
      darkBlueGrey: "#0F172A",
      BlueLightTextColor: "#B0ADCA",
      text: "#333333",
      black: "#121212",
      // Swagstore specific colors
      lightGrayBackground: "#F9F9F9",
      veryLightGray: "#F5F5F5",
      lightGrayBorder: "#ddd",
      mediumGrayText: "#666",
      hoverGray: "#f0f0f0",
      info: "#4D69FA",
      infoLightBorder: "#D1FAE5",
      infoLightBackground: "#ECFDF5",
      successGreen: "#10B981",
      successGreenLightBorder: "#D1FAE5",
      successGreenLightBackground: "#ECFDF5",
      purpleDark: "#7C3AED",
      teal: "#4FD1C5",
      purpleLight: "#A78BFA",
      pink: "#F687B3",
      grayText: "#6B7280",
      errorRed: "#EF4444",
      lightGray: "#E5E7EB",
      blueLightBackground: "#EFF6FF",
      mediumGray: "#9CA3AF",
      switchTrackGray: "#D1D5DB",
      shadowLight: "rgba(0,0,0,0.12)",
      shadowMedium: "rgba(0,0,0,0.1)",
      shadowVeryLight: "rgba(0, 0, 0, 0.05)",
      shadowLightBorder: "rgba(0, 0, 0, 0.08)",
      cardBackground: "#f8fafc",
      white: "#FFFFFF",
      purple: "#6455ED",
      greyBorder: "rgba(224, 224, 224, 1)",
      darkBlackBorder: "#000",
      blackText: "#121212",
      veryLightPurple: "rgba(163, 179, 255, 1)",
    },
  },
  typography: {
    fontFamily: "'Inter', 'Open Sans', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 600,
          borderRadius: "6px",
          padding: "6px 14px",
          minHeight: "34px",
          fontFamily: "'Inter', sans-serif",
          letterSpacing: "-0.005em",
        },
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            backgroundColor: "#1E293B",
            color: "#90CAF9",
            "&:hover": {
              backgroundColor: "#90CAF9",
              color: "#1E293B",
            },
            "&:disabled": {
              backgroundColor: "#4D69FA",
              color: "#fff",
              opacity: 0.4,
            },
          },
        },
        {
          props: { variant: "cancel" },
          style: {
            backgroundColor: "#fff",
            border: "1px solid #DC3545",
            color: "#DC3545",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#fff",
              color: "#DC3545",
              border: "1px solid #DC3545",
              boxShadow: "0px 4px 10px rgba(220, 53, 69, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "done" },
          style: {
            backgroundColor: "#28A745",
            color: "#fff",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#28A745",
              color: "#fff",
              boxShadow: "0px 4px 10px rgba(40, 167, 69, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            backgroundColor: "#4B5563",
            color: "#FCA5A5",
            "&:hover": {
              backgroundColor: "#FCA5A5",
              color: "#1E293B",
            },
          },
        },
        {
          props: { variant: "tertiary" },
          style: {
            backgroundColor: "#334155",
            color: "#34D399",
            "&:hover": {
              backgroundColor: "#34D399",
              color: "#1E293B",
            },
          },
        },
        {
          props: { variant: "grayscale" },
          style: {
            backgroundColor: "#1E293B",
            border: "1px solid #6B7280",
            color: "#6B7280",
            "&:hover": {
              backgroundColor: "#6B7280",
              color: "#1E293B",
            },
          },
        },
        {
          props: { variant: "transparentPrimary" },
          style: {
            backgroundColor: "transparent",
            color: "#4D69FA", // MUI default blue
            opacity: 1,
            border: "1px solid #4D69FA",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#4D69FA",
              opacity: 1,
            },
            "&:disabled": {
              backgroundColor: "transparent",
              color: "#4D69FA",
              opacity: 0.5,
            },
          },
        },
      ],
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          '&[role="menu"]': {
            paddingTop: 10,
            paddingBottom: 10,
            backgroundColor: "#2A2A2A",
            boxShadow: "10px 0 20px -12px rgba(115,115,115,0.8)",
          },
        },
      },
    },
  },
});
const blueTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#2196F3" },
    background: {
      default: "#0D47A1",
      paper: "#1565C0",
    },
    text: {
      primary: "#FFFFFF",
    },
    blue: {
      main: "#4049B1",
      light: lighten("#4049B1", 0.3),
      dark: darken("#4049B1", 0.3),
    },
    skyBlue: {
      main: "#4285F4",
      light: lighten("#4285F4", 0.3),
      dark: darken("#4285F4", 0.3),
    },
    purple: {
      main: "#8884D8",
      light: lighten("#8884D8", 0.3),
      dark: darken("#8884D8", 0.3),
    },
    mintGreen: {
      main: "#50DFC3",
      light: lighten("#50DFC3", 0.3),
      dark: darken("#50DFC3", 0.3),
    },
    green: {
      main: "#6CBA5C",
      light: lighten("#6CBA5C", 0.3),
      dark: darken("#6CBA5C", 0.3),
    },
    orange: {
      main: "#FF8042",
      light: lighten("#FF8042", 0.3),
      dark: darken("#FF8042", 0.3),
    },
    goldenYellow: {
      main: "#FFBB28",
      light: lighten("#FFBB28", 0.3),
      dark: darken("#FFBB28", 0.3),
    },
    brown: {
      main: "#FFE5B5",
      light: lighten("#FFE5B5", 0.3),
      dark: darken("#FFE5B5", 0.3),
    },
    blushPink: {
      main: "#FFCECE",
      light: lighten("#FFCECE", 0.3),
      dark: darken("#FFCECE", 0.3),
    },
    lavenderBlue: {
      main: "#D1CCFF",
      light: lighten("#D1CCFF", 0.3),
      dark: darken("#D1CCFF", 0.3),
    },
    red: {
      main: "#FF0000",
      light: lighten("#FF0000", 0.3),
      dark: darken("#FF0000", 0.3),
    },
    customColors: {
      lightYellow: "#F3EFDF",
      lightBlue: "#EDF0FF",
      darkBlue: "#4D69FA",
      pinkLight: "#FFB0D8",
      pinkDark: "#D7458F",
      greenLight: "#CCFFF5",
      greensuperLight: "#DFF3E4",
      greenDark: "#52E6CA",
      EmeraldGreen: "#02C875",
      greyTextColor: "#7C7A7F",
      backdrop: "rgba(0, 0, 0, 0.1)",
      blackBodyBackgorund: "#121212",
      greyBodyBackground: "#F3F3F3",
      lightGreyColor: "#F3F4F6",
      lightred: "#E96767",
      darkBlueText: "#1B2559",
      purpleMain: "#6455ED",
      lightOrange: "#ffe5c0",
      skyLight: "#00F0FF",
      darkBlueGrey: "#0F172A",
      BlueLightTextColor: "#B0ADCA",
      text: "#333333",
      black: "#121212",
      // Swagstore specific colors
      lightGrayBackground: "#F9F9F9",
      veryLightGray: "#F5F5F5",
      lightGrayBorder: "#ddd",
      mediumGrayText: "#666",
      hoverGray: "#f0f0f0",
      info: "#4D69FA",
      infoLightBorder: "#D1FAE5",
      infoLightBackground: "#ECFDF5",
      successGreen: "#10B981",
      successGreenLightBorder: "#D1FAE5",
      successGreenLightBackground: "#ECFDF5",
      purpleDark: "#7C3AED",
      teal: "#4FD1C5",
      purpleLight: "#A78BFA",
      pink: "#F687B3",
      grayText: "#6B7280",
      errorRed: "#EF4444",
      lightGray: "#E5E7EB",
      blueLightBackground: "#EFF6FF",
      mediumGray: "#9CA3AF",
      switchTrackGray: "#D1D5DB",
      shadowLight: "rgba(0,0,0,0.12)",
      shadowMedium: "rgba(0,0,0,0.1)",
      shadowVeryLight: "rgba(0, 0, 0, 0.05)",
      shadowLightBorder: "rgba(0, 0, 0, 0.08)",
      cardBackground: "#f8fafc",
      white: "#FFFFFF",
      purple: "#6455ED",
      greyBorder: "rgba(224, 224, 224, 1)",
      darkBlackBorder: "#000",
      blackText: "#121212",
      veryLightPurple: "rgba(163, 179, 255, 1)",
    },
  },
  typography: {
    fontFamily: "'Inter', 'Open Sans', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 600,
          borderRadius: "6px",
          padding: "6px 14px",
          minHeight: "34px",
          fontFamily: "'Inter', sans-serif",
          letterSpacing: "-0.005em",
        },
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            backgroundColor: "#1E293B",
            color: "#90CAF9",
            "&:hover": {
              backgroundColor: "#90CAF9",
              color: "#1E293B",
            },
            "&:disabled": {
              backgroundColor: "#4D69FA",
              color: "#fff",
              opacity: 0.4,
            },
          },
        },
        {
          props: { variant: "cancel" },
          style: {
            backgroundColor: "#fff",
            border: "1px solid #DC3545",
            color: "#DC3545",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#fff",
              color: "#DC3545",
              border: "1px solid #DC3545",
              boxShadow: "0px 4px 10px rgba(220, 53, 69, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "done" },
          style: {
            backgroundColor: "#28A745",
            color: "#fff",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#28A745",
              color: "#fff",
              boxShadow: "0px 4px 10px rgba(40, 167, 69, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            backgroundColor: "#4B5563",
            color: "#FCA5A5",
            "&:hover": {
              backgroundColor: "#FCA5A5",
              color: "#1E293B",
            },
          },
        },
        {
          props: { variant: "tertiary" },
          style: {
            backgroundColor: "#334155",
            color: "#34D399",
            "&:hover": {
              backgroundColor: "#34D399",
              color: "#1E293B",
            },
          },
        },
        {
          props: { variant: "grayscale" },
          style: {
            backgroundColor: "#1E293B",
            border: "1px solid #6B7280",
            color: "#6B7280",
            "&:hover": {
              backgroundColor: "#6B7280",
              color: "#1E293B",
            },
          },
        },
        {
          props: { variant: "transparentPrimary" },
          style: {
            backgroundColor: "transparent",
            color: "#4D69FA", // MUI default blue
            opacity: 1,
            border: "1px solid #4D69FA",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#4D69FA",
              opacity: 1,
            },
            "&:disabled": {
              backgroundColor: "transparent",
              color: "#4D69FA",
              opacity: 0.5,
            },
          },
        },
      ],
    },
  },
});
const greenTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#388E3C" },
    background: {
      default: "#E8F5E9",
      paper: "#C8E6C9",
    },
    text: {
      primary: "#1B5E20",
    },
    blue: {
      main: "#4049B1",
      light: lighten("#4049B1", 0.3),
      dark: darken("#4049B1", 0.3),
    },
    skyBlue: {
      main: "#4285F4",
      light: lighten("#4285F4", 0.3),
      dark: darken("#4285F4", 0.3),
    },
    purple: {
      main: "#8884D8",
      light: lighten("#8884D8", 0.3),
      dark: darken("#8884D8", 0.3),
    },
    mintGreen: {
      main: "#50DFC3",
      light: lighten("#50DFC3", 0.3),
      dark: darken("#50DFC3", 0.3),
    },
    green: {
      main: "#6CBA5C",
      light: lighten("#6CBA5C", 0.3),
      dark: darken("#6CBA5C", 0.3),
    },
    orange: {
      main: "#FF8042",
      light: lighten("#FF8042", 0.3),
      dark: darken("#FF8042", 0.3),
    },
    goldenYellow: {
      main: "#FFBB28",
      light: lighten("#FFBB28", 0.3),
      dark: darken("#FFBB28", 0.3),
    },
    brown: {
      main: "#FFE5B5",
      light: lighten("#FFE5B5", 0.3),
      dark: darken("#FFE5B5", 0.3),
    },
    blushPink: {
      main: "#FFCECE",
      light: lighten("#FFCECE", 0.3),
      dark: darken("#FFCECE", 0.3),
    },
    lavenderBlue: {
      main: "#D1CCFF",
      light: lighten("#D1CCFF", 0.3),
      dark: darken("#D1CCFF", 0.3),
    },
    red: {
      main: "#FF0000",
      light: lighten("#FF0000", 0.3),
      dark: darken("#FF0000", 0.3),
    },
    customColors: {
      lightYellow: "#F3EFDF",
      lightBlue: "#EDF0FF",
      darkBlue: "#4D69FA",
      pinkLight: "#FFB0D8",
      pinkDark: "#D7458F",
      greenLight: "#CCFFF5",
      greensuperLight: "#DFF3E4",
      greenDark: "#52E6CA",
      EmeraldGreen: "#02C875",
      greyTextColor: "#7C7A7F",
      backdrop: "rgba(0, 0, 0, 0.1)",
      tableGrayHeadingColor: "#93969B",
      blackBodyBackgorund: "#121212",
      greyBodyBackground: "#F3F3F3",
      lightGreyColor: "#F3F4F6",
      lightred: "#E96767",
      darkBlueText: "#1B2559",
      purpleMain: "#6455ED",
      lightOrange: "#ffe5c0",
      skyLight: "#00F0FF",
      darkBlueGrey: "#0F172A",
      BlueLightTextColor: "#B0ADCA",
      text: "#333333",
      black: "#121212",
      // Swagstore specific colors
      lightGrayBackground: "#F9F9F9",
      veryLightGray: "#F5F5F5",
      lightGrayBorder: "#ddd",
      mediumGrayText: "#666",
      hoverGray: "#f0f0f0",
      info: "#4D69FA",
      infoLightBorder: "#D1FAE5",
      infoLightBackground: "#ECFDF5",
      successGreen: "#10B981",
      successGreenLightBorder: "#D1FAE5",
      successGreenLightBackground: "#ECFDF5",
      purpleDark: "#7C3AED",
      teal: "#4FD1C5",
      purpleLight: "#A78BFA",
      pink: "#F687B3",
      grayText: "#6B7280",
      errorRed: "#EF4444",
      lightGray: "#E5E7EB",
      blueLightBackground: "#EFF6FF",
      mediumGray: "#9CA3AF",
      switchTrackGray: "#D1D5DB",
      shadowLight: "rgba(0,0,0,0.12)",
      shadowMedium: "rgba(0,0,0,0.1)",
      shadowVeryLight: "rgba(0, 0, 0, 0.05)",
      shadowLightBorder: "rgba(0, 0, 0, 0.08)",
      cardBackground: "#f8fafc",
      white: "#FFFFFF",
      purple: "#6455ED",
      greyBorder: "rgba(224, 224, 224, 1)",
      darkBlackBorder: "#000",
      blackText: "#121212",
    },
  },
  typography: {
    fontFamily: "'Inter', 'Open Sans', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 600,
          borderRadius: "6px",
          padding: "6px 14px",
          minHeight: "34px",
          fontFamily: "'Inter', sans-serif",
          letterSpacing: "-0.005em",
        },
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            backgroundColor: "#EDF0FF",
            color: "#4D69FA",
            "&:hover": {
              backgroundColor: "#4D69FA",
              color: "#fff",
            },
            "&:disabled": {
              backgroundColor: "#4D69FA",
              color: "#fff",
              opacity: 0.4,
            },
          },
        },
        {
          props: { variant: "cancel" },
          style: {
            backgroundColor: "#fff",
            border: "1px solid #DC3545",
            color: "#DC3545",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#fff",
              color: "#DC3545",
              border: "1px solid #DC3545",
              boxShadow: "0px 4px 10px rgba(220, 53, 69, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "done" },
          style: {
            backgroundColor: "#28A745",
            color: "#fff",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#28A745",
              color: "#fff",
              boxShadow: "0px 4px 10px rgba(40, 167, 69, 0.4)",
              transform: "translateY(-2px)",
            },
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            backgroundColor: "#FFB0D8",
            color: "#D7458F",
            "&:hover": {
              backgroundColor: "#D7458F",
              color: "#fff",
            },
          },
        },
        {
          props: { variant: "tertiary" },
          style: {
            backgroundColor: "#CCFFF5",
            color: "#52E6CA",
            "&:hover": {
              backgroundColor: "#52E6CA",
              color: "#fff",
            },
          },
        },
        {
          props: { variant: "grayscale" },
          style: {
            backgroundColor: "#fff",
            border: "1px solid #93969B",
            color: "#93969B",
            "&:hover": {
              backgroundColor: "#93969B",
              color: "#fff",
            },
          },
        },
        {
          props: { variant: "transparentPrimary" },
          style: {
            backgroundColor: "transparent",
            color: "#EDF0FF", // MUI default blue
            opacity: 1,
            border: "1px solid #EDF0FF",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#EDF0FF",
              opacity: 1,
            },
            "&:disabled": {
              backgroundColor: "transparent",
              color: "#EDF0FF",
              opacity: 0.5,
            },
          },
        },
      ],
    },
  },
});
export const themes = {
  light: lightTheme,
  dark: darkTheme,
  blue: blueTheme,
  green: greenTheme,
};
export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined,
);
export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [themeName, setThemeName] = useState<keyof typeof themes>("light");
  const theme = useMemo(() => themes[themeName], [themeName]);
  useEffect(() => {
    document.body.style.backgroundColor =
      theme.palette.mode === "light"
        ? theme.palette.customColors.greyBodyBackground
        : theme.palette.customColors.blackBodyBackgorund;

    document.body.style.transition = "background-color 0.3s ease-in-out";
  }, [theme]);
  return (
    <ThemeContext.Provider value={{ themeName, setThemeName }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
